import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import styles from "@/components/notes-popup.module.css";
import NotesToolbar from "@/components/notes-toolbar";
import PopupShell from "@/components/popup-shell";
import { getNewNoteTemplate } from "@/data/new-note-template";
import useCustomInput from "@/hooks/use-custom-input";
import useNotes from "@/hooks/use-notes";
import * as signals from "@/signals";
import { DialogIcon, NoteInfo, NoteSavingStatus } from "@/types";
import { closePopup, handleKeyboardClick, showDialog } from "@/utils";
import { faCalendar, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, MouseEvent, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface NotesPopupProps {
	isShown: boolean;
}

function NotesPopup({ isShown }: NotesPopupProps): JSX.Element {
	const queryClient = useQueryClient();

	const debounceTimeoutId = useRef<number | null>(null);
	const isComposing = useRef<boolean>(false);

	const debounce = (
		func: (...args: unknown[]) => void,
		wait: number,
	): ((...args: unknown[]) => void) => {
		return (...args: unknown[]) => {
			if (debounceTimeoutId.current) {
				window.clearTimeout(debounceTimeoutId.current);
				debounceTimeoutId.current = null;
			}
			debounceTimeoutId.current = window.setTimeout(() => {
				func(...args);
			}, wait);
		};
	};

	const setNoteValue = (newValue: string): void => {
		const newCurrentNode = {
			...signals.currentNote.value,
			content: newValue,
			modifiedAt: new Date().toISOString(),
		};
		signals.currentNote.value = newCurrentNode;
		setSavingStatus(
			isComposing.current
				? NoteSavingStatus.UNSAVED
				: NoteSavingStatus.SAVING,
		);
		countWords(newValue);
		debounce(() => {
			if (isComposing.current) {
				return;
			}
			void saveNote(newCurrentNode);
		}, 1000)();
	};

	const noteArea = useCustomInput<HTMLTextAreaElement>({
		extraMenuItems: [
			{
				content: {
					icon: faCalendar,
					text: t("dateCountdown"),
				},
				onClick: (): void => {
					const cursorPosition = noteArea.ref.current?.selectionStart;
					if (cursorPosition === undefined) {
						return;
					}
					const toInsert = "[toTime yyyy-MM-dd]";
					const newContent =
						signals.currentNote.value.content.slice(
							0,
							cursorPosition,
						) +
						toInsert +
						signals.currentNote.value.content.slice(cursorPosition);
					setNoteValue(newContent);
				},
				when: true,
			},
		],
		setValue: setNoteValue,
	});

	const [isNoteAreaShown, setIsNoteAreaShown] = useState<boolean>(false);
	const [isSplitEditorEnabled, setIsSplitEditorEnabled] =
		useState<boolean>(false);
	const [isStatusHighlighted, setIsStatusHighlighted] =
		useState<boolean>(false);
	const [savingStatus, setSavingStatus] = useState<NoteSavingStatus>(
		NoteSavingStatus.SAVED,
	);
	const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);
	const [wordCount, setWordCount] = useState<string>("0");

	const { data, isError, isFetching } = useNotes();

	const isCurrentNoteNew = signals.currentNote.value.id === "new-note";
	const isNoteOpened =
		!!signals.currentNote.value.content || !isCurrentNoteNew;

	const closeNote = (): void => {
		setIsNoteAreaShown(false);
		createNote();
	};

	const closeNotesPopup = (): void => {
		closePopup("notes");
	};

	const countWords = (text: string): void => {
		const regExp = /[\d|\w]+|[\u4E00-\uFA29]/gi;
		const all = (text.match(regExp) || []).length;
		const selectedText = getSelectedText();
		if (selectedText) {
			setWordCount((selectedText.match(regExp) || []).length + "/" + all);
		} else {
			setWordCount(all.toString());
		}
	};

	const createNote = (): void => {
		signals.currentNote.value = getNewNoteTemplate();
		setSavingStatus(NoteSavingStatus.UNSAVED);
		setSelectedNoteIds([]);
		setWordCount("0");
	};

	const deleteNote = async (): Promise<void> => {
		if (selectedNoteIds.length > 0) {
			return deleteNotes();
		}
		await showDialog(t("confirmDeleteThisNote"), {
			icon: DialogIcon.WARNING,
			showCancel: true,
			title: t("confirm"),
		});
		setIsNoteAreaShown(false);
		if (isCurrentNoteNew) {
			createNote();
			return;
		}
		signals.isLoadingShown.value = true;
		try {
			await new QueryDataManager(queryClient).deleteNote(
				signals.currentNote.value.id,
			);
			createNote();
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	const deleteNotes = async (): Promise<void> => {
		await showDialog(t("confirmDeleteTheseNotes"), {
			icon: DialogIcon.WARNING,
			showCancel: true,
			title: t("confirm"),
		});
		signals.isLoadingShown.value = true;
		try {
			const notesToDelete = new Set(selectedNoteIds);
			if (!isCurrentNoteNew) {
				notesToDelete.add(signals.currentNote.value.id);
			}
			await new QueryDataManager(queryClient).deleteNotes(notesToDelete);
			createNote();
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	const getSelectedText = (): string => {
		if (!noteArea.ref.current) {
			return "";
		}
		const { selectionStart, selectionEnd } = noteArea.ref.current;
		return noteArea.ref.current.value.substring(
			selectionStart,
			selectionEnd,
		);
	};

	const handleCompositionEnd = (): void => {
		isComposing.current = false;
		setSavingStatus(NoteSavingStatus.SAVING);
	};

	const handleCompositionStart = (): void => {
		isComposing.current = true;
	};

	const handleMarkdownButtonClick = (): void => {
		const newCurrentNode: NoteInfo = {
			...signals.currentNote.value,
			itemType:
				signals.currentNote.value.itemType === "md" ? "txt" : "md",
		};
		signals.currentNote.value = newCurrentNode;
		if (!isCurrentNoteNew) {
			void saveNote(newCurrentNode);
		}
	};

	const handleNewNoteButtonClick = (): void => {
		createNote();
		setIsNoteAreaShown(true);
	};

	const handleNoteAreaMouseUp = (): void => {
		window.setTimeout(() => {
			if (!getSelectedText()) {
				countWords(signals.currentNote.value.content);
			}
		}, 1);
	};

	const handleNoteAreaSelect = (): void => {
		countWords(signals.currentNote.value.content);
	};

	const openNote = (note: NoteInfo): void => {
		signals.currentNote.value = note;
		setIsNoteAreaShown(true);
		setSavingStatus(NoteSavingStatus.SAVED);
		setSelectedNoteIds([]);
		countWords(note.content);
	};

	const saveNote = async (note: NoteInfo): Promise<void> => {
		try {
			const queryDataManager = new QueryDataManager(queryClient);
			if (isCurrentNoteNew) {
				const { id } = await queryDataManager.createNote(note);
				signals.currentNote.value = {
					...note,
					id: id,
				};
			} else {
				await queryDataManager.editNote(note);
			}
			setSavingStatus(NoteSavingStatus.SAVED);
		} catch (error) {
			void api.handleApiError(error);
		}
		setIsStatusHighlighted(true);
		window.setTimeout(() => {
			setIsStatusHighlighted(false);
		}, 1000);
	};

	const toggleNotePin = async (): Promise<void> => {
		if (signals.currentNote.value.id === "new-note") {
			console.error("Cannot pin a new note");
			return;
		}
		signals.isLoadingShown.value = true;
		const newCurrentNote = {
			...signals.currentNote.value,
			isPinned: !signals.currentNote.value.isPinned,
		};
		const queryDataManager = new QueryDataManager(queryClient);
		if (signals.currentNote.value.isPinned) {
			await queryDataManager.unpinNote(signals.currentNote.value);
		} else {
			await queryDataManager.pinNote(signals.currentNote.value);
		}
		signals.currentNote.value = newCurrentNote;
		signals.isLoadingShown.value = false;
	};

	const toggleSplitEditor = (): void => {
		setIsSplitEditorEnabled(!isSplitEditorEnabled);
	};

	const noteList: NoteInfo[] = [];
	if (isCurrentNoteNew && signals.currentNote.value.content) {
		// Show the new note at the top of the list
		noteList.push(signals.currentNote.value);
	}
	if (data?.results) {
		noteList.push(...data.results);
	}

	const noteListElements = noteList.map((note) => {
		const handleClick = (event?: MouseEvent): void => {
			if (signals.currentNote.value.id === note.id) {
				setSelectedNoteIds([]);
				return;
			}
			if (event && (event.ctrlKey || event.metaKey)) {
				// multi-select
				setSelectedNoteIds((selectedNoteIds) => {
					if (selectedNoteIds.includes(note.id)) {
						return selectedNoteIds.filter((id) => id !== note.id);
					}
					return [...selectedNoteIds, note.id];
				});
				return;
			}
			openNote(note);
		};

		return (
			<li
				aria-selected={
					note.id === signals.currentNote.value.id ||
					selectedNoteIds.includes(note.id)
				}
				className={styles["note-item"]}
				key={note.id}
				role="option"
				tabIndex={0}
				onClick={handleClick}
				onKeyDown={handleKeyboardClick(handleClick)}
			>
				<div className={styles["note-title"]}>
					{note.title || note.content || t("blankNote")}
				</div>
				<div className={styles["note-time"]}>
					{new Date(note.modifiedAt).toLocaleString(undefined, {
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit",
						month: "numeric",
						year: "numeric",
					})}
				</div>
			</li>
		);
	});

	useEffect(() => {
		if (!isShown) {
			return;
		}
		signals.isLoadingShown.value = isFetching;
	}, [isFetching, isShown]);

	return (
		<PopupShell
			isHeadingHidden={true}
			isMaximizeEnabled={true}
			isPaddingNarrow={true}
			isPureWhite={true}
			isShown={isShown}
			onClose={closeNotesPopup}
			title={t("notes")}
		>
			<div
				className={clsx(
					styles["notes-container"],
					isNoteAreaShown && styles["note-opened"],
				)}
			>
				<div className={styles["notes-list-container"]}>
					<button
						className={styles["new-note-button"]}
						onClick={handleNewNoteButtonClick}
						type="button"
					>
						<FontAwesomeIcon icon={faPlus} />
						{t("newNote")}
					</button>
					{noteList.length > 0 ? (
						<ul className={styles["notes-list"]}>
							{noteListElements}
						</ul>
					) : (
						!isFetching && (
							<div className={styles["notes-list"]}>
								<div className={styles["placeholder"]}>
									{isError
										? t("errorOccurredSyncNotes")
										: t("youHaveNoNotesCurrently")}
								</div>
							</div>
						)
					)}
				</div>
				<div className={styles["editor-area-container"]}>
					<NotesToolbar
						canDeleteNote={
							isNoteOpened || selectedNoteIds.length > 0
						}
						canPinNote={isNoteOpened && !isCurrentNoteNew}
						closeNote={closeNote}
						deleteNote={deleteNote}
						isMarkdownEnabled={
							signals.currentNote.value.itemType === "md"
						}
						isNoteOpened={isNoteOpened}
						isNotePinned={
							signals.currentNote.value.isPinned || false
						}
						isSplitEditorEnabled={isSplitEditorEnabled}
						isStatusHighlighted={isStatusHighlighted}
						onMarkdownButtonClick={handleMarkdownButtonClick}
						savingStatus={savingStatus}
						toggleNotePin={toggleNotePin}
						toggleSplitEditor={toggleSplitEditor}
						wordCount={wordCount}
					/>
					<div
						className={clsx(
							styles["text-area-container"],
							signals.currentNote.value.itemType === "md" &&
								isSplitEditorEnabled &&
								styles["split"],
						)}
					>
						<textarea
							className="input"
							hidden={
								signals.currentNote.value.itemType === "md" &&
								!isSplitEditorEnabled
							}
							onCompositionEnd={handleCompositionEnd}
							onCompositionStart={handleCompositionStart}
							onMouseUp={handleNoteAreaMouseUp}
							onSelect={handleNoteAreaSelect}
							placeholder={t("typeHereToCreateNewNote")}
							value={signals.currentNote.value.content}
							{...noteArea}
						></textarea>
						{signals.currentNote.value.itemType === "md" && (
							<div className={styles["markdown-area"]}>
								<ReactMarkdown remarkPlugins={[remarkGfm]}>
									{signals.currentNote.value.content}
								</ReactMarkdown>
							</div>
						)}
					</div>
				</div>
			</div>
		</PopupShell>
	);
}

export default NotesPopup;
